// base behaviour contract — stub implementations.
//
// base contains NO implementation for these behaviours. Every framework
// theme that extends base (bootstrap5, future zitTailwind, etc.) MUST
// replace this module via its own index.js BEFORE hiveMind.main() runs:
//
//   const hiveMind = require('base/.../hiveMind');
//   hiveMind.behaviors = require('./scripts/behaviors');  // theme's own
//   hiveMind.app       = require('./scripts/app');
//   hiveMind.main();
//
// Each hook below corresponds to a JS API that one or more base
// components depend on. See behaviors.contract.md (sibling file) for the
// signature, lifecycle, and return shape that each hook must satisfy.
//
// Calling any stub throws — running base without a framework theme is
// not a supported configuration and we want it to fail loudly the first
// time a component tries to open a modal / popover / etc.
//
// One exception: `datepicker` ships a REAL native implementation (not a
// stub), because the native HTML5 date input is framework-neutral and is
// the zero-dependency default; themes override it only to swap in a richer
// JS picker. See its inline comment and behaviors.contract.md.

// Build a stub function that throws a precise, named error.
const notImplemented = (hookName) => () => {
    throw new Error(
        'zit behaviour not implemented: ' + hookName +
        ' — extend base with a framework theme that overrides hm.behaviors'
    );
};

// The contract surface. Sub-objects mirror the JS APIs base components
// historically called directly (bootstrap.Modal, KTMenu, KTBlockUI, etc.).
// Factory hooks (create / get) return an instance object with the methods
// listed in the contract doc; one-shot hooks (toast.show, breakpoint.isUp,
// submenu.createInstances) act directly.
const behaviors = {
    modal: {
        create: notImplemented('modal.create')                    // (el) -> { show, hide, dispose }
    },
    collapse: {
        create: notImplemented('collapse.create'),                // (el, opts) -> { show, hide, dispose }
        // Resolve a runtime collapse-state class name to the tier's literal
        // token. Accepts 'root' (the marker class that says "this element
        // participates in collapse"), 'shown' (the class applied while the
        // region is expanded) and 'hidden' (applied while collapsed). The
        // returned string may contain whitespace-separated tokens; callers
        // that hand it to classList.add/remove must .split(' ') first.
        // Static decoration like "this region is user-collapsible" is NOT
        // a stateClass — it lives in cssClassesMap (see windowCollapsible).
        stateClass: notImplemented('collapse.stateClass'),        // ('root'|'shown'|'hidden') -> string
        // Declarative auto-init for collapsible regions inside rootEl. Walks
        // rootEl (inclusive — rootEl itself is inspected, then descendants)
        // and applies the tier's collapse state classes ('root' + 'shown' or
        // 'hidden') to every [data-zit-collapsible] element, based on the
        // sibling [data-zit-initially-collapsed="true|false"] attribute
        // (default false → initially expanded). Idempotent: elements already
        // processed are skipped via the data-zit-collapse-initialized marker.
        // Triggers ([data-zit-toggle="collapse"]) are NOT wired here — those
        // continue to be handled by bridgeTierAttrs + the tier's auto-init
        // delegate (Bootstrap's document-level click handler in zb5; a future
        // tier without an equivalent delegate MUST extend this hook).
        // rootEl defaults to document for the boot-time bulk scan; consumers
        // that insert dynamic markup pass the inserted subtree.
        createInstances: notImplemented('collapse.createInstances') // (rootEl?) -> void
    },
    toast: {
        show: notImplemented('toast.show')                        // (el) -> void
    },
    popover: {
        create: notImplemented('popover.create')                  // (el, opts) -> { show, hide, dispose }
    },
    submenu: {
        createInstances: notImplemented('submenu.createInstances'),  // () -> void  (bulk init)
        get: notImplemented('submenu.get')                        // (el) -> { on, show } | null
    },
    drawer: {
        get: notImplemented('drawer.get')                         // (el) -> { show, hide, destroy } | null
    },
    blockUI: {
        create: notImplemented('blockUI.create')                  // (el) -> { block, release, destroy }
    },
    breakpoint: {
        isUp: notImplemented('breakpoint.isUp')                   // (name) -> boolean
    },
    // Image-input widget: file picker with live preview, plus change / cancel
    // / remove affordances. Wraps the per-tier upload widget (KTImageInput in
    // metronic_8, a Bootstrap-flavoured fallback in bootstrap5).
    imageInput: {
        create: notImplemented('imageInput.create')               // (el) -> { dispose }
    },
    // Password-meter widget: visibility toggle and (optional) strength
    // indicator. Wraps the per-tier widget (KTPasswordMeter in metronic_8).
    passwordMeter: {
        create: notImplemented('passwordMeter.create')            // (el) -> { dispose }
    },
    // Tag-input widget: turns a text input into a multi-select tags field
    // (chips, autocomplete dropdown, optional free typing). Wraps the
    // per-tier widget (Tagify in metronic_8); base ships no implementation,
    // so a tier that has not bundled one (e.g. cleanSlate) throws on first
    // use — selectMultiple is non-functional there until a tags adapter
    // is provided. See behaviors.contract.md for the create config + handle.
    tags: {
        create: notImplemented('tags.create')                     // (el, {options, freeTags}) -> { getValues, selectAll, selectNone }
    },
    // Date / datetime picker. UNLIKE every other hook above, base ships a
    // REAL implementation here, not a throwing stub: the native HTML5
    // date / datetime-local input is framework-neutral and fully functional
    // on its own, so it is the zero-dependency default. A framework theme
    // overrides this hook only to SUBSTITUTE a richer JS picker (metronic_8
    // wraps tempusDominus). Used by date, datetime and date_interval.
    // create() turns the element into the native picker and returns a handle;
    // values flow as ISO strings (date -> "YYYY-MM-DD", datetime-local ->
    // "YYYY-MM-DDTHH:MM:SS"), which is what every consumer submits.
    datepicker: {
        // (el, {time, minDate, maxDate, format, locale, startOfTheWeek, disabledDaysOfTheWeek})
        //   -> { getValue(): isoString, setMin(iso|''), setMax(iso|''), dispose() }
        create: function (el, opts) {
            opts = opts || {};

            // Native input type: datetime-local when the field carries a time, date otherwise.
            el.type = opts.time ? 'datetime-local' : 'date';

            // Seconds granularity for datetime-local so the value round-trips "YYYY-MM-DDTHH:MM:SS".
            if (opts.time) {
                el.step = '1';
            }

            // min / max arrive already formatted for the chosen input type (the template emits
            // date bounds as "YYYY-MM-DD" and datetime bounds with the "T" separator).
            if (opts.minDate) {
                el.min = opts.minDate;
            }
            if (opts.maxDate) {
                el.max = opts.maxDate;
            }

            // Native input needs no teardown; the handle just reads/writes the element directly.
            return {
                getValue: function () { return el.value; },
                setMin: function (iso) { el.min = iso || ''; },
                setMax: function (iso) { el.max = iso || ''; },
                dispose: function () {}
            };
        }
    },
    // Activation state — runtime classes the framework toggles on UI
    // controls to mark them "active" (current page in a pagination, picked
    // option in selectSearchable, etc.) or "disabled" (un-interactable
    // control). Same shape as collapse.stateClass: returned string is the
    // tier's literal token(s); callers using classList.add/remove must
    // .split(' ') first. Decorative variants ("default-styled button vs
    // primary-styled button") are NOT activation state — they live in
    // cssClassesMap.
    activation: {
        stateClass: notImplemented('activation.stateClass')       // ('active'|'disabled') -> string
    },
    // Tier-optional markup bridge. Tiers whose underlying framework auto-
    // discovers DOM markup via its own data-* namespace (e.g. bootstrap5
    // bridging data-zit-* to data-bs-* for Bootstrap auto-init of collapse
    // triggers, tooltip triggers and toast dismiss buttons) implement this
    // to walk rootEl's subtree and copy zit-namespaced markers onto their
    // framework-namespaced equivalents. Idempotent (only writes when the
    // destination key is unset). base consumers that insert dynamic
    // markup containing such markers call it on the new subtree. Tiers
    // without auto-discovery markup (e.g. a CSS-only Tailwind tier) leave
    // the no-op default in place.
    bridgeTierAttrs: function (/* rootEl */) {}                   // (rootEl) -> void
};

module.exports = behaviors;
